import type { PermissionCategory } from '@/constants/permissions';
import adminRoutes from '@/router/adminRoutes';
import authRoutes from '@/router/authRoutes';
import homeRoutes from '@/router/homeRoutes';
import loadingRoutes from '@/router/loadingRoutes';
import menuRoutes from '@/router/menuRoutes';
import myRecipesRoutes from '@/router/myRecipesRoutes';
import onboardingRoutes from '@/router/onboardingRoutes';
import ordersRoutes from '@/router/ordersRoutes';
import planningRoutes from '@/router/planningRoutes';
import profileRoutes from '@/router/profileRoutes';
import searchRoutes from '@/router/searchRoutes';
import suppliersRoutes from '@/router/suppliersRoute';
import { useAppStore } from '@/stores/appStore';
import { useRouterStore } from '@/stores/routerStore';
import { useUserStore } from '@/stores/useUserStore';
import { hasSubscriptionFeature } from '@/utils/auth';
import { endPerformanceTrace, measurePerformance, printPerformanceSummary, startPerformanceTrace } from '@/utils/performance';
import HomeView from '@/views/HomeView.vue';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';
import employeeRoutes from './employeeRoutes';
import subscriptionRoutes from './subscriptionRoute';

// Deep link handling state
let deepLinkChecked = false;

// Valid domains for deep links
const validDomains = ['app.gastronomia.ai', 'dev-gastronomia.backtik.ch', 'staging-gastronomia.backtik.ch', 'gastronomia.backtik.ch'];

/**
 * Extract the path from a deep link URL
 */
function extractPathFromDeepLinkUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    if (validDomains.includes(urlObj.hostname)) {
      return urlObj.pathname + urlObj.search; // Include query params
    }
    console.warn('[Router] URL hostname not recognized:', urlObj.hostname);
    return null;
  } catch (error) {
    console.error('[Router] Failed to parse URL:', url, error);
    return null;
  }
}

/**
 * Check for launch URL on native platforms (called once on first navigation)
 */
async function checkLaunchUrl(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  try {
    const launchUrl = await CapacitorApp.getLaunchUrl();
    if (launchUrl?.url) {
      console.log('[Router] App launched with URL:', launchUrl.url);
      return extractPathFromDeepLinkUrl(launchUrl.url);
    }
  } catch (error) {
    console.error('[Router] Failed to check launch URL:', error);
  }
  return null;
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...adminRoutes,
    ...authRoutes,
    ...homeRoutes,
    ...loadingRoutes,
    ...menuRoutes,
    ...onboardingRoutes,
    ...ordersRoutes,
    ...planningRoutes,
    ...profileRoutes,
    ...searchRoutes,
    ...myRecipesRoutes,
    ...suppliersRoutes,
    ...employeeRoutes,
    ...subscriptionRoutes,
    { path: '/:pathMatch(.*)*', name: 'NotFound', component: HomeView },
  ],
  scrollBehavior(to) {
    if (to.name !== 'generation-history')
      return {
        top: 0,
        // behavior: 'smooth',
      };
  },
});

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  console.log('from : ', from);
  const navigationTrace = `startup:router.beforeEach:${to.fullPath}`;
  startPerformanceTrace(navigationTrace, {
    from: from.fullPath,
    to: to.fullPath,
  });

  const appStore = useAppStore();

  function finalizeNavigation<T>(result: T, metadata?: Record<string, unknown>) {
    appStore.appReady = true;
    endPerformanceTrace(navigationTrace, metadata);
    printPerformanceSummary('startup:', 12);
    return result;
  }

  // Check for deep link on first navigation (native platforms only)
  if (!deepLinkChecked && Capacitor.isNativePlatform()) {
    deepLinkChecked = true;
    const deepLinkPath = await measurePerformance('startup:router.checkLaunchUrl', () => checkLaunchUrl(), {
      route: to.fullPath,
    });

    if (deepLinkPath && deepLinkPath !== '/' && deepLinkPath !== to.path) {
      console.log('[Router] Redirecting to deep link path:', deepLinkPath);
      return finalizeNavigation(deepLinkPath, { redirectedTo: deepLinkPath });
    }
  }

  if (!to.meta.footerConfig || !to.meta.headerConfig || !to.meta.securityConfig) {
    console.log('missing meta');
    const userStore = useUserStore();
    if (userStore.isEmployee) {
      return finalizeNavigation({ name: 'home' }, { reason: 'missing-meta-employee' });
    }
    return finalizeNavigation({ name: 'home' }, { reason: 'missing-meta' });
  }

  const userStore = useUserStore();

  // Only initialize user if authentication is required
  if (to.meta.securityConfig.requireAuthentication) {
    await measurePerformance('startup:router.initUser', () => userStore.initUser(), {
      route: to.fullPath,
    });

    // If initUser failed to get a user (authentication failed), clear everything
    if (!userStore.user) {
      userStore.clearUserData();
      return finalizeNavigation({ name: 'login' }, { reason: 'missing-user' });
    }
  } else {
    // For public routes, just mark app as ready without attempting authentication
    appStore.appReady = true;
  }

  // **NEW: Check if user has restaurants**
  if (
    to.meta.securityConfig.requireAuthentication &&
    userStore.user &&
    userStore.user.restaurants.length === 0 &&
    to.name !== 'no-restaurant' &&
    to.name !== 'logout'
  ) {
    console.log('[Router] User has no restaurants, redirecting to no-restaurant');
    return finalizeNavigation({ name: 'no-restaurant' }, { reason: 'no-restaurant' });
  }

  // **Check registration completion**
  if (
    to.meta.securityConfig.requireAuthentication &&
    userStore.user &&
    !userStore.user.registration_completed &&
    to.name !== 'validate-restaurant' &&
    to.name !== 'logout' &&
    to.name !== 'no-restaurant'
  ) {
    console.log('[Router] Registration incomplete, redirecting to validate-restaurant');
    return finalizeNavigation({ name: 'validate-restaurant' }, { reason: 'registration-incomplete' });
  }

  // Conditionally update the restaurant information
  await measurePerformance('startup:router.updateRestaurantInfo', () => userStore.updateRestaurantInfo(), {
    route: to.fullPath,
  });

  const homePage = userStore.isEmployee ? 'home' : 'home';

  if (to.meta.securityConfig.requireAuthentication) {
    if (userStore.user) {
      if (to.meta.securityConfig.rolesAllowed.includes(userStore.user.role)) {
        if (to.meta.securityConfig.restaurantRoleAllowed && userStore.activeRestaurant && userStore.activeRestaurant.role) {
          const restaurantRoleAllowed = to.meta.securityConfig.restaurantRoleAllowed;
          if (!restaurantRoleAllowed.includes(userStore.activeRestaurant.role)) {
            console.log(`User's restaurant role ${userStore.activeRestaurant.role} not allowed for this route`);
            return finalizeNavigation({ name: homePage }, { reason: 'restaurant-role-denied' });
          }
        }

        if (to.meta.securityConfig.requiredPermissions) {
          if (userStore.activeRestaurant && userStore.activeRestaurant.role === 'admin') {
            console.log('Restaurant admin bypassing permission checks');
          } else {
            const permissions = to.meta.securityConfig.requiredPermissions;

            if (Array.isArray(permissions)) {
              const hasAnyPermission = permissions.some(({ category, action }) => userStore.hasPermission(category as PermissionCategory, action));

              if (!hasAnyPermission) {
                console.log(`User lacks any of the required permissions`);
                return finalizeNavigation({ name: homePage }, { reason: 'permissions-denied' });
              }
            } else {
              const { category, action } = permissions;
              if (!userStore.hasPermission(category as PermissionCategory, action)) {
                console.log(`User lacks permission: ${category}.${action}`);
                return finalizeNavigation({ name: homePage }, { reason: 'permission-denied', permission: `${category}.${action}` });
              }
            }
          }
        }

        if (to.meta.securityConfig?.requiredFeatures) {
          const requiredFeatures = to.meta.securityConfig.requiredFeatures as Array<{ feature: string; minValue?: unknown }>;
          const user = userStore.user;
          const allFeaturesOk = requiredFeatures.every(({ feature, minValue }) => hasSubscriptionFeature(user, feature, minValue));
          if (!allFeaturesOk) {
            return finalizeNavigation({ name: homePage }, { reason: 'feature-denied' });
          }
        }
        console.log('User authorized');
        return finalizeNavigation(true, { authorized: true });
      }
      console.log('User not authorized');
      return finalizeNavigation({ name: homePage }, { reason: 'role-denied' });
    } else {
      console.log('User not logged in');
      return finalizeNavigation({ name: 'login' }, { reason: 'not-logged-in' });
    }
  } else {
    if ((to.name === 'login' || to.name === 'register' || to.name === 'forgot-password') && userStore.user) {
      return finalizeNavigation({ name: homePage }, { reason: 'already-authenticated' });
    } else {
      return finalizeNavigation(true, { publicRoute: true });
    }
  }
});

router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  useRouterStore().previousPath = from.path;
});

export default router;
